/**
 * WebAuthn ceremony helpers — registration (create) and authentication (get).
 *
 * These functions wrap navigator.credentials.create/get and translate the
 * ArrayBuffer results into base64url-encoded payloads ready for transport
 * to the Edge Functions.
 *
 * The challenge is **always** server-issued via the `webauthn-challenge`
 * Edge Function. The browser never invents one. This binds every ceremony
 * to a fresh server gesture so a captured / forged client_data_json
 * cannot be replayed against the verify endpoint.
 *
 * Relying Party ID defaults to the current hostname. The server enforces
 * its own configured RP id and origin during the verify step — if they
 * mismatch the ceremony is rejected.
 */

import { bufferToBase64Url, base64UrlToBuffer }  from './encoding'
import type {
  EnrollPayload,
  VerifyPayload,
  ChallengeResponse,
}                                                from './types'
import type { SupabaseClient }                   from '@supabase/supabase-js'

const RP_NAME = 'ReadyRoomOps'

/**
 * Derives the Relying Party ID from the current window location.
 * In production this is the bare hostname (e.g. "app.readyroomops.com").
 * Localhost is accepted for local development.
 */
function rpId(): string {
  return window.location.hostname
}

/**
 * Asks the server to mint a fresh WebAuthn challenge. Throws if the
 * Edge Function returns an error — callers must surface the failure
 * rather than fall back to a client-generated challenge (which would
 * defeat the whole anti-replay binding).
 */
async function fetchServerChallenge(
  supabase: SupabaseClient,
  purpose:  'enroll' | 'verify' | 'signin',
): Promise<ChallengeResponse> {
  const { data, error } = await supabase.functions.invoke('webauthn-challenge', {
    body: { purpose },
  })
  if (error) {
    throw new Error(`webauthn-challenge failed: ${error.message}`)
  }
  const c = data as Partial<ChallengeResponse>
  if (typeof c?.challenge !== 'string' || typeof c?.expires_at !== 'string' || typeof c?.rp_id !== 'string') {
    throw new Error('webauthn-challenge returned an invalid payload')
  }
  return c as ChallengeResponse
}

export interface RegistrationOptions {
  supabase:   SupabaseClient
  userId:     string
  userName:   string
  deviceName: string
}

/**
 * Runs the WebAuthn registration ceremony (navigator.credentials.create)
 * and returns a serialised payload for the webauthn-enroll Edge Function.
 *
 * Throws if the user cancels or the authenticator rejects.
 */
export async function createRegistration(
  options: RegistrationOptions,
): Promise<EnrollPayload> {
  const challenge   = await fetchServerChallenge(options.supabase, 'enroll')
  const userIdBytes = new TextEncoder().encode(options.userId)

  const credential = await navigator.credentials.create({
    publicKey: {
      rp: {
        name: RP_NAME,
        id:   challenge.rp_id || rpId(),
      },
      user: {
        id:          userIdBytes,
        name:        options.userName,
        displayName: options.userName,
      },
      challenge: base64UrlToBuffer(challenge.challenge),
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },    // ES256 (ECDSA w/ SHA-256)
        { type: 'public-key', alg: -257 },  // RS256 (RSASSA-PKCS1-v1_5 w/ SHA-256)
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification:        'required',
        residentKey:             'preferred',
      },
      timeout: 60_000,
      attestation: 'none',
    },
  }) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('Registration was cancelled or no credential was returned.')
  }

  const response = credential.response as AuthenticatorAttestationResponse

  const transports: string[] =
    typeof response.getTransports === 'function'
      ? response.getTransports()
      : []

  // Modern browsers expose getAuthenticatorData(); when present we send
  // it so the server can verify rpIdHash + UP flag without CBOR-decoding
  // the attestation object. Older browsers omit this field.
  const getAuthData = (response as AuthenticatorAttestationResponse & {
    getAuthenticatorData?: () => ArrayBuffer
  }).getAuthenticatorData
  const authenticatorData = typeof getAuthData === 'function'
    ? bufferToBase64Url(getAuthData.call(response))
    : undefined

  return {
    credential_id:      bufferToBase64Url(credential.rawId),
    public_key:         bufferToBase64Url(response.getPublicKey?.() ?? response.attestationObject),
    device_name:        options.deviceName,
    transports,
    attestation_object: bufferToBase64Url(response.attestationObject),
    client_data_json:   bufferToBase64Url(response.clientDataJSON),
    ...(authenticatorData ? { authenticator_data: authenticatorData } : {}),
  }
}

export interface AuthenticationOptions {
  supabase: SupabaseClient
  /** Base64url-encoded credential IDs the user has registered. */
  allowCredentials: Array<{ id: string; transports?: string[] }>
}

/**
 * Runs the WebAuthn authentication ceremony (navigator.credentials.get)
 * and returns a serialised payload for the webauthn-verify Edge Function.
 *
 * Throws if the user cancels or the authenticator rejects.
 */
export async function getAssertion(
  options: AuthenticationOptions,
): Promise<VerifyPayload> {
  const challenge = await fetchServerChallenge(options.supabase, 'verify')

  const allowCredentials: PublicKeyCredentialDescriptor[] =
    options.allowCredentials.map((c) => ({
      type:       'public-key' as const,
      id:         base64UrlToBuffer(c.id),
      transports: (c.transports ?? []) as AuthenticatorTransport[],
    }))

  const credential = await navigator.credentials.get({
    publicKey: {
      challenge:        base64UrlToBuffer(challenge.challenge),
      rpId:             challenge.rp_id || rpId(),
      allowCredentials,
      userVerification: 'required',
      timeout:          60_000,
    },
  }) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('Authentication was cancelled or no assertion was returned.')
  }

  const response = credential.response as AuthenticatorAssertionResponse

  return {
    credential_id:      bufferToBase64Url(credential.rawId),
    authenticator_data: bufferToBase64Url(response.authenticatorData),
    client_data_json:   bufferToBase64Url(response.clientDataJSON),
    signature:          bufferToBase64Url(response.signature),
  }
}

export interface SigninCeremonyOptions {
  supabase: SupabaseClient
}

/**
 * Cold-open WebAuthn sign-in ceremony — passwordless. Uses a discoverable
 * credential (residentKey: 'preferred' was set during enrollment), so
 * `allowCredentials` is empty and the OS picks the credential to use
 * via FaceID / fingerprint / Windows Hello.
 *
 * The challenge is fetched from the unauthenticated `webauthn-challenge`
 * endpoint with purpose='signin'. The returned assertion is sent to
 * `webauthn-signin` (also unauthenticated) which resolves the user from
 * the credential_id and mints an OTP for verifyOtp redemption.
 */
export async function getSigninAssertion(
  options: SigninCeremonyOptions,
): Promise<VerifyPayload> {
  const challenge = await fetchServerChallenge(options.supabase, 'signin')

  const credential = await navigator.credentials.get({
    publicKey: {
      challenge:        base64UrlToBuffer(challenge.challenge),
      rpId:             challenge.rp_id || rpId(),
      allowCredentials: [],   // empty → OS picks via discoverable credential UI
      userVerification: 'required',
      timeout:          60_000,
    },
  }) as PublicKeyCredential | null

  if (!credential) {
    throw new Error('Authentication was cancelled or no assertion was returned.')
  }

  const response = credential.response as AuthenticatorAssertionResponse

  return {
    credential_id:      bufferToBase64Url(credential.rawId),
    authenticator_data: bufferToBase64Url(response.authenticatorData),
    client_data_json:   bufferToBase64Url(response.clientDataJSON),
    signature:          bufferToBase64Url(response.signature),
  }
}
